import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type IntersectionObserverWrapperProps = {
  autoDisconnect?: boolean;
  callBack?: () => void;
  children:
    | ((isVisible: boolean) => React.ReactNode)
    | Array<(isVisible: boolean) => React.ReactNode>;
  options?: IntersectionObserverInit;
};

const defaultProps = {
  autoDisconnect: true,
  callBack: () => {},
  children: [],
  options: {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: [0, 1]
  }
};

const IntersectionObserverWrapper = ({
  autoDisconnect = defaultProps.autoDisconnect,
  callBack = defaultProps.callBack,
  children = defaultProps.children,
  options = defaultProps.options
}: IntersectionObserverWrapperProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const container = useRef<HTMLDivElement | null>(null);

  const disconnect = useCallback(() => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }
  }, []);

  const handleObservations = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];

      if (entry.intersectionRatio > 0) {
        setIsVisible(entry.isIntersecting);
        callBack();

        if (autoDisconnect) {
          disconnect();
        }
      }
    },
    [autoDisconnect, callBack, disconnect]
  );

  const memoizedOptions = useMemo(() => options, [options]);

  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      handleObservations,
      memoizedOptions
    );

    if (container.current) {
      intersectionObserver.current.observe(container.current);
    }

    return () => {
      disconnect();
    };
  }, [handleObservations, memoizedOptions, disconnect]);

  return (
    <div ref={container}>
      {Array.isArray(children)
        ? children.map((child: (isVisible: boolean) => React.ReactNode) =>
            child(isVisible)
          )
        : children(isVisible)}
    </div>
  );
};

export default IntersectionObserverWrapper;
